import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./MenuExame.css";
import api from "../../servicos/api";
import { formatarValor } from "../../utils/formatarValor";
import { normalizarAcentos } from "../../utils/normalizarAcentos";
import useDebounce from "../../hooks/useDebounce";
import { useExames } from "../../context/ExamesContext";
import {
  TextField,
  Container,
  InputAdornment,
  CircularProgress,
  Box,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import ExameItem from "./components/ExameItem";

//O menu exame oferece a opção de pesquisa por parte do nome do exame, mostrando uma lista com todos os exames
function MenuExame() {
  const navigate = useNavigate();
  const [exames, setExames] = useState([]);
  const [nome, setNome] = useState("");
  const [nomePesquisa, setNomePesquisa] = useState(""); // Termo de pesquisa atual
  const nomePesquisaDebounced = useDebounce(nomePesquisa, 500);
  const [examesExpandidos, setExamesExpandidos] = useState(new Set());

  const { examesSelecionados: examesSelecionadosCompleto, toggleExame, atualizarExame } = useExames();

  const examesSelecionados = useMemo(() => {
    return new Set(examesSelecionadosCompleto.keys());
  }, [examesSelecionadosCompleto]);
  const [limit] = useState(50);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  // Otimização: useCallback para evitar recriação de funções
  const handleToggleExpandir = useCallback((exameId) => {
    setExamesExpandidos((prev) => {
      const novosExpandidos = new Set(prev);
      novosExpandidos.has(exameId) ? novosExpandidos.delete(exameId) : novosExpandidos.add(exameId);
      return novosExpandidos;
    });
  }, []);

  const handleToggleSelecao = useCallback((exameId, e) => {
    e.stopPropagation();
    
    const exame = exames.find((e) => e.id === exameId);
    if (exame) {
      toggleExame(exameId, exame);
    }
  }, [exames, toggleExame]);

  // Otimização: useMemo para cálculo do total
  const totalSelecionado = useMemo(() => {
    let total = 0;
    examesSelecionadosCompleto.forEach((exame) => {
      const preco = parseFloat(exame.preco) || 0;
      total += preco;
    });
    return total;
  }, [examesSelecionadosCompleto]);

  const carregarExamesEPrecos = useCallback(async (termoPesquisa = "", pagina = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Normalizar acentos e converter para maiúsculas
      const nomeNormalizado = termoPesquisa
        ? normalizarAcentos(termoPesquisa).toUpperCase()
        : "";

      const urlExames = nomeNormalizado
        ? `/api/exames/${nomeNormalizado}?page=${pagina}&limit=${limit}&comPrecos=true`
        : `/api/exames?page=${pagina}&limit=${limit}&comPrecos=true`;

      // Buscar exames
      const examesResponse = await api.get(urlExames);

      const examesData = examesResponse.data.data || examesResponse.data;
      const paginationData = examesResponse.data.pagination || {
        page: pagina,
        limit,
        total: examesData.length,
        totalPages: 1,
      };

      if (append) {
        setExames((prev) => [...prev, ...examesData]);
      } else {
        setExames(examesData);
      }
      
      const examesSelecionadosAtual = new Map(examesSelecionadosCompleto);
      examesData.forEach((exame) => {
        if (examesSelecionadosAtual.has(exame.id)) {
          atualizarExame(exame.id, exame);
        }
      });
      
      setPagination(paginationData);
      setHasMore(pagina < paginationData.totalPages);
    } catch (error) {
      console.error("Erro ao carregar exames:", error);
      if (!append) {
        setExames([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, atualizarExame]);

  //Carrega exames quando termo de pesquisa muda
  useEffect(() => {
    setHasMore(true);
    setPagination({ page: 1, limit: 50, total: 0, totalPages: 0 });
    isLoadingMoreRef.current = false;
    carregarExamesEPrecos(nomePesquisaDebounced, 1, false);
  }, [nomePesquisaDebounced, carregarExamesEPrecos]);

  //Atualiza a página de acordo com o filtro solicitado
  function procurarExames(e) {
    e.preventDefault();
    const valorInput = e.target.value;
    setNome(valorInput);
    const nomeNormalizado = valorInput
      ? normalizarAcentos(valorInput).toUpperCase()
      : "";
    setNomePesquisa(nomeNormalizado);
  }

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const valorNormalizado = nome
        ? normalizarAcentos(nome).toUpperCase()
        : "";
      setNomePesquisa(valorNormalizado);
      carregarExamesEPrecos(valorNormalizado, 1, false);
    }
  }, [nome, carregarExamesEPrecos]);

  // Carrega mais exames quando chega ao final da página
  const carregarMaisExames = useCallback(() => {
    if (isLoadingMoreRef.current || loadingMore || loading || !hasMore) {
      return;
    }
    
    if (pagination.page >= pagination.totalPages) {
      setHasMore(false);
      return;
    }
    
    isLoadingMoreRef.current = true;
    const proximaPage = pagination.page + 1;
    carregarExamesEPrecos(nomePesquisa, proximaPage, true);
  }, [loadingMore, loading, hasMore, pagination, nomePesquisa, carregarExamesEPrecos]);

  // Intersection Observer para detectar quando está próximo do final
  useEffect(() => {
    const currentSentinel = sentinelRef.current;
    if (!currentSentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          carregarMaisExames();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    observer.observe(currentSentinel);

    return () => {
      observer.disconnect();
    };
  }, [carregarMaisExames]);

  const handleNavigateResumo = useCallback(() => {
    navigate("/exames/resumo");
  }, [navigate]);

  return (
    <Container maxWidth="lg">
      <div
        style={{
          paddingTop: "80px",
          paddingBottom: examesSelecionados.size > 0 ? "80px" : "0",
        }}
      >
        <div className="pesquisa">
          <TextField
            placeholder="Informe o nome do exame"
            fullWidth
            variant="outlined"
            onChange={procurarExames}
            onKeyDown={handleKeyDown}
            value={nome}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="searchIcon" />
                </InputAdornment>
              ),
            }}
            className="pesquisaInput"
          />
        </div>

        {loading && (
          <div className="itemVazio" style={{ textAlign: "center", padding: "20px" }}>
            Carregando exames...
          </div>
        )}

        {!loading && (
          <>
            <div className="groupItem">
              {exames.map((exame, index) => (
                <ExameItem
                  key={exame.id}
                  exame={exame}
                  index={index}
                  totalExames={exames.length}
                  isExpandido={examesExpandidos.has(exame.id)}
                  isSelecionado={examesSelecionados.has(exame.id)}
                  onToggleExpandir={handleToggleExpandir}
                  onToggleSelecao={handleToggleSelecao}
                />
              ))}

              {exames.length === 0 && (
                <div className="itemVazio">Nenhum exame encontrado...</div>
              )}
            </div>

            {/* Sentinel para scroll infinito */}
            {(hasMore || loadingMore) && (
              <Box
                ref={sentinelRef}
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding={3}
                minHeight="60px"
              >
                {loadingMore && <CircularProgress size={24} />}
              </Box>
            )}

            {/* Indicador de fim da lista */}
            {!hasMore && exames.length > 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding={2}
                color="text.secondary"
                fontSize="0.875rem"
              >
                Todos os exames foram carregados ({exames.length} de {pagination.total})
              </Box>
            )}
          </>
        )}

        {examesSelecionados.size > 0 && (
          <div 
            className="previewBar"
            onClick={handleNavigateResumo}
          >
            <div className="previewBarContent">
              <div className="previewBarInfo">
                <div className="previewBarTitle">Pré-visualização dos exames</div>
                <div className="previewBarValue">{formatarValor(totalSelecionado)}</div>
              </div>
              <KeyboardArrowRightIcon className="previewBarArrow" />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}

export default MenuExame;
